import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Form,
  List,
  Select,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  FileTextOutlined,
  RedoOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { createM2MHandoff, listM2MHandoffs, retryM2MHandoff, m2mErrorMessage, safeExternalM2MUrl } from './m2mApi';
import { useM2MIdentity } from './useM2MIdentity';
import M2MReportViewer from './M2MReportViewer';
import FeatherSpinner from '../components/FeatherSpinner';
import { DEEP_DIVE_PRICE, DEEP_DIVE_VALUE } from './deepDiveOfferContent';
import type { MarketingActionSignal, M2MHandoffRecord } from './types';
import { canonicalizeLanguageKey } from '../utils/languageIdentity';
import { getTargetLanguageFromPair, splitLanguagePair } from '../utils/languagePairs';
import {
  getM2MBlockingHandoff,
  isM2MSupportedLanguage,
  M2M_TARGET_LANGUAGE_OPTIONS,
} from './m2mLanguages';

const { Text } = Typography;

interface M2MReportsModuleProps {
  apiBase: string;
  bookId: string;
  userId: string;
  bookTitle?: string;
  bookLanguage?: string;
  selectedLanguage?: string;
  completedTranslationPairs?: string[];
  actionSignal?: MarketingActionSignal | null;
  initialPrimaryGenre?: string;
  initialBookTone?: string;
  initialHeatLevel?: string;
  initialGenreDescription?: string;
  initialNotes?: string;
  displayMode?: 'combined' | 'setup' | 'reports';
  refreshSignal?: number;
  showHeader?: boolean;
  showRefreshAction?: boolean;
  emptyState?: React.ReactNode;
  existingHandoffs?: M2MHandoffRecord[];
  onHandoffsChange?: (handoffs: M2MHandoffRecord[]) => void;
  onHandoffCreated?: (handoff?: M2MHandoffRecord) => void;
}

const STATUS_LABELS: Record<string, string> = {
  created: 'Handoff Created',
  awaiting_payment: 'Awaiting Payment',
  paid: 'Paid',
  processing: 'Processing at M2M',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const statusColor = (status?: string): string => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'completed' || normalized === 'paid') return 'success';
  if (normalized === 'failed' || normalized === 'cancelled' || normalized === 'expired') return 'error';
  if (normalized === 'processing') return 'processing';
  if (normalized === 'awaiting_payment' || normalized === 'created') return 'warning';
  return 'default';
};

const M2M_PACKAGE_TYPE = 'deep_dive';

const primaryGenreOptions = [
  'Romance',
  'Fantasy',
  'Science Fiction',
  'Mystery',
  'Thriller / Suspense',
  'Contemporary Fiction',
  'Historical Fiction',
].map((value) => ({ label: value, value }));

const bookToneOptions = [
  'Dark & Intense',
  'Witty & Humorous',
  'Sweet & Wholesome',
  'Emotional & Angsty',
  'Steamy & Spicy',
  'Action-Packed',
  'Cozy & Comforting',
  'Inspirational & Uplifting',
  'Suspenseful & Gripping',
].map((value) => ({ label: value, value }));

const heatLevelOptions = [
  { value: '1', label: '1 - Perfectly innocent' },
  { value: '2', label: '2 - Sweet' },
  { value: '3', label: '3 - Low heat' },
  { value: '4', label: '4 - Warm' },
  { value: '5', label: '5 - Closed door intimacy' },
  { value: '6', label: '6 - Open door light' },
  { value: '7', label: '7 - Steamy' },
  { value: '8', label: '8 - Spicy' },
  { value: '9', label: '9 - Very spicy' },
  { value: '10', label: '10 - Completely explicit' },
];

const inferSourceLanguage = (selectedLanguage?: string, bookLanguage?: string): string => {
  const selected = (selectedLanguage || '').trim();
  if (selected && selected.toLowerCase() !== 'original') {
    const { sourceLang } = splitLanguagePair(selected);
    if (sourceLang) return sourceLang;
  }
  return (bookLanguage || 'English').trim() || 'English';
};

const buildTranslatedVersion = (
  selectedLanguage: string | undefined,
  bookLanguage: string | undefined,
  targetLanguage: string | undefined,
): string => {
  const source = inferSourceLanguage(selectedLanguage, bookLanguage);
  const target = (targetLanguage || '').trim();
  if (!source || !target || source.toLowerCase() === target.toLowerCase()) {
    return '';
  }
  return `${source}-${target}`;
};

const M2MReportsWorkspace: React.FC<M2MReportsModuleProps> = ({
  apiBase,
  bookId,
  userId,
  bookTitle,
  bookLanguage,
  selectedLanguage,
  completedTranslationPairs,
  actionSignal,
  initialPrimaryGenre,
  initialBookTone,
  initialHeatLevel,
  initialGenreDescription,
  initialNotes,
  displayMode = 'combined',
  refreshSignal = 0,
  showHeader = true,
  showRefreshAction = true,
  emptyState,
  existingHandoffs = [],
  onHandoffsChange,
  onHandoffCreated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(displayMode !== 'setup');
  const [handoffs, setHandoffs] = useState<M2MHandoffRecord[]>([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);

  const [reportId, setReportId] = useState<string>();
  const active = useRef(true);
  const listRequest = useRef<AbortController>();
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; listRequest.current?.abort(); };
  }, []);

  const [primaryGenre, setPrimaryGenre] = useState<string>(() => initialPrimaryGenre || '');
  const [bookTone, setBookTone] = useState<string>(() => initialBookTone || '');
  const [heatLevel, setHeatLevel] = useState<string>(() => initialHeatLevel || '');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [consentAccepted, setConsentAccepted] = useState<boolean>(false);

  const lastProcessedActionTsRef = useRef<number | null>(null);
  const showSetup = displayMode !== 'reports';
  const showReports = displayMode !== 'setup';
  const completedTargetLanguageKeys = useMemo(() => {
    const keys = new Set<string>();
    (completedTranslationPairs || []).forEach((pair) => {
      const target = getTargetLanguageFromPair(pair);
      const key = canonicalizeLanguageKey(target);
      if (key) keys.add(key);
    });
    return keys;
  }, [completedTranslationPairs]);

  const isCompletedTargetLanguage = useCallback(
    (language: string): boolean => completedTargetLanguageKeys.has(canonicalizeLanguageKey(language)),
    [completedTargetLanguageKeys]
  );

  const knownHandoffs = useMemo(() => {
    const byId = new Map<string, M2MHandoffRecord>();
    [...existingHandoffs, ...handoffs].forEach((handoff) => byId.set(handoff.handoffId, handoff));
    return Array.from(byId.values());
  }, [existingHandoffs, handoffs]);

  const hasExistingReport = useCallback(
    (language: string): boolean => Boolean(getM2MBlockingHandoff(knownHandoffs, language)),
    [knownHandoffs]
  );

  const availableTargetLanguageOptions = useMemo(
    () => {
      const options = M2M_TARGET_LANGUAGE_OPTIONS.map((option) => {
        const translationIncomplete = !isCompletedTargetLanguage(option.value);
        const reportAlreadyOrdered = hasExistingReport(option.value);
        const disabled = translationIncomplete || reportAlreadyOrdered;
        return {
          ...option,
          label: reportAlreadyOrdered
            ? `${option.label} - report already ordered`
            : translationIncomplete
              ? `${option.label} - translation not complete`
              : option.label,
          disabled,
        };
      });
      return options.sort((left, right) => {
        if (left.disabled !== right.disabled) return left.disabled ? 1 : -1;
        return left.value.localeCompare(right.value, undefined, { sensitivity: 'base' });
      });
    },
    [hasExistingReport, isCompletedTargetLanguage]
  );

  const sourceLanguage = useMemo(
    () => inferSourceLanguage(selectedLanguage, bookLanguage),
    [bookLanguage, selectedLanguage]
  );
  const activeReportTargetLanguage = useMemo(
    () => getTargetLanguageFromPair(selectedLanguage),
    [selectedLanguage]
  );
  const visibleHandoffs = useMemo(() => {
    const activeKey = canonicalizeLanguageKey(activeReportTargetLanguage);
    if (!activeKey) return handoffs;
    return handoffs.filter((handoff) =>
      (handoff.targetLanguages || []).some(
        (language) => canonicalizeLanguageKey(language) === activeKey
      )
    );
  }, [activeReportTargetLanguage, handoffs]);

  useEffect(() => {
    const inferred = getTargetLanguageFromPair(selectedLanguage);
    if (
      !inferred ||
      !isM2MSupportedLanguage(inferred) ||
      !isCompletedTargetLanguage(inferred) ||
      hasExistingReport(inferred)
    ) {
      setTargetLanguages((prev) => (prev.length ? [] : prev));
      return;
    }
    setTargetLanguages((prev) => {
      const current = prev[0];
      if (current && isCompletedTargetLanguage(current)) return prev;
      return [inferred];
    });
  }, [hasExistingReport, isCompletedTargetLanguage, selectedLanguage]);

  const translatedVersion = useMemo(
    () => buildTranslatedVersion(selectedLanguage, bookLanguage, targetLanguages[0]),
    [bookLanguage, selectedLanguage, targetLanguages]
  );
  const canSubmit = Boolean(
    consentAccepted &&
    targetLanguages.length === 1 &&
    isM2MSupportedLanguage(targetLanguages[0]) &&
    isCompletedTargetLanguage(targetLanguages[0]) &&
    !hasExistingReport(targetLanguages[0]) &&
    translatedVersion.trim() &&
    primaryGenre &&
    bookTone &&
    heatLevel
  );

  const refreshHandoffs = useCallback(async (background = false) => {
    if (!bookId || !userId) return;
    if (background && listRequest.current) return;
    if (!background) {
      setIsLoading(true);
      setReportsLoaded(false);
    }
    listRequest.current?.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    try {
      const data = await listM2MHandoffs({ apiBase, bookId, userId }, controller.signal);
      if (!active.current || controller.signal.aborted) return;
      const nextHandoffs = Array.isArray(data?.items) ? data.items : [];
      setHandoffs(nextHandoffs);
      setReportsLoaded(!data.hasMore);
      onHandoffsChange?.(nextHandoffs);
    } catch (error) {
      if (active.current && !controller.signal.aborted && !background) message.error(m2mErrorMessage(error, 'Failed to load Deep Dive reports.'));
    } finally {
      if (listRequest.current === controller) listRequest.current = undefined;
      if (active.current && !controller.signal.aborted && !background) setIsLoading(false);
    }
  }, [apiBase, bookId, onHandoffsChange, userId]);

  useEffect(() => {
    if (showReports) {
      void refreshHandoffs();
    }
  }, [refreshHandoffs, refreshSignal, showReports]);

  useEffect(() => {
    if (!actionSignal?.ts) return;
    if (lastProcessedActionTsRef.current === actionSignal.ts) return;
    lastProcessedActionTsRef.current = actionSignal.ts;

    if (showReports && actionSignal.action === 'refresh') {
      void refreshHandoffs();
    }
  }, [actionSignal, refreshHandoffs, showReports]);

  const hasActiveOrders = handoffs.some(handoff =>
    ['created', 'awaiting_payment', 'paid', 'processing'].includes((handoff.status || '').toLowerCase()));
  useEffect(() => {
    if (!showReports) return;
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'hidden') void refreshHandoffs(true);
    };
    const timer = window.setInterval(refreshWhenVisible, hasActiveOrders ? 10000 : 30000);
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener('online', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [hasActiveOrders, refreshHandoffs, showReports]);

  const submitHandoff = useCallback(async () => {
    if (!consentAccepted) {
      message.error('You must confirm consent before continuing.');
      return;
    }

    if (targetLanguages.length !== 1) {
      message.error('Select a completed translated language for this M2M handoff.');
      return;
    }

    if (!isCompletedTargetLanguage(targetLanguages[0])) {
      message.error('Select a completed translated language for this M2M handoff.');
      return;
    }

    if (!isM2MSupportedLanguage(targetLanguages[0])) {
      message.error('M2M Deep Dive reports are not available for this language.');
      return;
    }

    if (hasExistingReport(targetLanguages[0])) {
      message.error('A Deep Dive report is already in progress or completed for this language.');
      return;
    }

    const resolvedTranslatedVersion = translatedVersion.trim();
    if (!resolvedTranslatedVersion) {
      message.error('Choose a target language different from the source language.');
      return;
    }

    if (!primaryGenre || !bookTone || !heatLevel) {
      message.error('Select a primary genre, book tone, and heat level for the M2M form.');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await createM2MHandoff({ apiBase, bookId, userId }, {
            userId,
            packageType: M2M_PACKAGE_TYPE,
            targetLanguages,
            primaryGenre,
            bookTone,
            genreDescription: initialGenreDescription,
            heatLevel,
            notes: initialNotes,
            shareSelection: {
              includeOriginal: false,
              includeTranslated: true,
              translatedVersion: resolvedTranslatedVersion,
            },
            consent: {
              accepted: consentAccepted,
              consentVersion: 'm2m_handoff_v1',
            },
      });
      if (!active.current) return;
      const handoff = data?.handoff as M2MHandoffRecord | undefined;
      if (handoff?.handoffId) {
        setHandoffs((prev) => [handoff, ...prev.filter((item) => item.handoffId !== handoff.handoffId)]);
      }

      const checkoutUrl = safeExternalM2MUrl(data?.checkoutUrl || handoff?.m2m?.checkoutUrl);
      message.success('M2M handoff created. Continue checkout in M2M.');
      if (checkoutUrl) {
        window.open(String(checkoutUrl), '_blank', 'noopener,noreferrer');
      }
      onHandoffCreated?.(handoff);
    } catch (error) {
      if (active.current) message.error(m2mErrorMessage(error, 'Failed to create Deep Dive order.'));
    } finally {
      if (active.current) setIsSubmitting(false);
    }
  }, [
    apiBase,
    bookId,
    consentAccepted,
    primaryGenre,
    bookTone,
    initialGenreDescription,
    heatLevel,
    initialNotes,
    hasExistingReport,
    isCompletedTargetLanguage,
    targetLanguages,
    translatedVersion,
    userId,
    onHandoffCreated,
  ]);

  const retryHandoff = useCallback(
    async (handoffId: string) => {
      try {
        const data = await retryM2MHandoff({ apiBase, bookId, userId }, handoffId);
        if (!active.current) return;
        const handoff = data?.handoff as M2MHandoffRecord | undefined;
        if (handoff?.handoffId) {
          const nextHandoffs = handoffs.some((item) => item.handoffId === handoff.handoffId)
            ? handoffs.map((item) => (item.handoffId === handoff.handoffId ? handoff : item))
            : [handoff, ...handoffs];
          setHandoffs(nextHandoffs);
          onHandoffsChange?.(nextHandoffs);
        }
        const checkoutUrl = safeExternalM2MUrl(data?.checkoutUrl || handoff?.m2m?.checkoutUrl);
        message.success('M2M handoff retried.');
        if (checkoutUrl) {
          window.open(String(checkoutUrl), '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        if (active.current) message.error(m2mErrorMessage(error, 'Failed to retry Deep Dive order.'));
      }
    },
    [apiBase, bookId, handoffs, onHandoffsChange, userId]
  );


  return (
    <div className={`m2m-reports-module m2m-reports-module--${displayMode}`}>
      {showSetup ? (
      <section className="translation-modal-block translation-modal-block--scope">
        <div className="translation-modal-block-header">
          <div>
            <h3>Deep Dive details</h3>
            <p>
              Novel Publicity uses the selected completed translation and these details to prepare
              its marketing analysis. Your ScribeShadow-exclusive price is {DEEP_DIVE_PRICE} ({DEEP_DIVE_VALUE} value), paid on Novel Publicity after continuing. The finished report will be emailed
              to you and returned to this workspace.
            </p>
          </div>
        </div>

        <Form layout="vertical" className="m2m-reports-form">
          <div className="m2m-reports-form-grid">
            <div className="metadata-translation-modal-setup-cell metadata-translation-modal-setup-cell--readonly">
              <span className="translation-modal-kicker">Book</span>
              <strong>{bookTitle || 'Untitled'}</strong>
            </div>

            <div className="metadata-translation-modal-setup-cell metadata-translation-modal-setup-cell--readonly">
              <span className="translation-modal-kicker">Source language</span>
              <strong>{sourceLanguage}</strong>
            </div>

            <Form.Item label="Target language" className="m2m-reports-form-item">
              <Select
                aria-label="Target language"
                value={targetLanguages[0] || undefined}
                onChange={(value) => setTargetLanguages(value ? [value] : [])}
                options={availableTargetLanguageOptions}
                placeholder="Select language"
              />
            </Form.Item>

            <Form.Item label="Primary genre" className="m2m-reports-form-item">
              <Select
                aria-label="Primary genre"
                value={primaryGenre || undefined}
                onChange={(value) => setPrimaryGenre(value)}
                options={primaryGenreOptions}
                placeholder="Select genre"
              />
            </Form.Item>

            <Form.Item label="Book tone or vibe" className="m2m-reports-form-item">
              <Select
                aria-label="Book tone or vibe"
                value={bookTone || undefined}
                onChange={(value) => setBookTone(value)}
                options={bookToneOptions}
                placeholder="Select tone"
              />
            </Form.Item>

            <Form.Item label="Romance heat level" className="m2m-reports-form-item">
              <Select
                aria-label="Romance heat level"
                value={heatLevel || undefined}
                onChange={(value) => setHeatLevel(value)}
                options={heatLevelOptions}
                placeholder="Select heat level"
              />
            </Form.Item>
          </div>

          <Form.Item className="m2m-reports-consent">
            <Checkbox checked={consentAccepted} onChange={(event) => setConsentAccepted(event.target.checked)}>
              I authorize ScribeShadow to share this translated manuscript with Novel Publicity for this report.
            </Checkbox>
          </Form.Item>

          <div className="m2m-reports-actions">
            {isSubmitting ? <div className="m2m-reports-loading-box">
              <FeatherSpinner style={{ width: 32, height: 32 }} alt="Preparing your translated manuscript…" />
              <span>Preparing your translated manuscript…</span>
            </div> : <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => void submitHandoff()}
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              Continue to Novel Publicity checkout
            </Button>}
          </div>
        </Form>
      </section>
      ) : null}

      {showReports ? (
      <section
        className="m2m-reports-library"
        aria-labelledby={showHeader ? 'm2m-report-history-title' : undefined}
        aria-label={showHeader ? undefined : 'Deep Dive reports and orders'}
      >
        {showHeader ? (
          <div className="m2m-reports-library__header">
            <div>
              <h3 id="m2m-report-history-title">Reports and orders</h3>
              <p>Completed reports and active M2M orders for this book.</p>
            </div>
            {showRefreshAction ? (
              <Button
                aria-label="Refresh M2M reports"
                icon={<ReloadOutlined />}
                onClick={() => void refreshHandoffs()}
                loading={isLoading}
              >
                Refresh
              </Button>
            ) : null}
          </div>
        ) : null}
        {isLoading && visibleHandoffs.length === 0 ? (
          <div className="m2m-reports-loading-box" aria-label="Loading M2M reports">
            <FeatherSpinner style={{ width: 32, height: 32 }} alt="Loading Deep Dive reports…" />
            <span>Loading Deep Dive reports…</span>
          </div>
        ) : visibleHandoffs.length === 0 && reportsLoaded && emptyState ? emptyState : visibleHandoffs.length === 0 ? (
          <div className="m2m-reports-library__empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={activeReportTargetLanguage
                ? `No Deep Dive reports or orders for ${activeReportTargetLanguage} yet.`
                : 'No Deep Dive reports or orders yet.'}
            />
          </div>
        ) : (
          <List
            className="m2m-reports-handoff-list"
            dataSource={visibleHandoffs}
            renderItem={(handoff) => {
              const status = (handoff.status || '').toLowerCase();
              const statusLabel = STATUS_LABELS[status] || (handoff.status || 'Unknown');
              const isRetryable = status === 'failed' || status === 'cancelled' || status === 'expired';
              const checkoutUrl = safeExternalM2MUrl(handoff.m2m?.checkoutUrl);
              const storedReport = Boolean(handoff.m2m?.reportBlobName || handoff.m2m?.reportFormat === 'html' || handoff.m2m?.reportUrl?.startsWith('/api/') || (handoff.m2m?.reportId && !handoff.m2m?.reportUrl));
              const externalReport = storedReport ? null : safeExternalM2MUrl(handoff.m2m?.reportUrl);
              const hasReport = storedReport || Boolean(externalReport);
              const targetLabel = handoff.targetLanguages?.filter(Boolean).join(', ') || 'Translation';
              const dateValue = handoff.m2m?.reportReceivedAt || handoff.completedAt || handoff.createdAt;
              const dateLabel = dateValue ? new Date(dateValue).toLocaleString() : 'Date unavailable';
              const canContinueCheckout = Boolean(
                checkoutUrl && (status === 'created' || status === 'awaiting_payment')
              );

              return (
                <List.Item key={handoff.handoffId}>
                  <div className="m2m-reports-handoff">
                    <div className="m2m-reports-handoff__main">
                      <span className="m2m-reports-handoff__icon" aria-hidden="true">
                        <FileTextOutlined />
                      </span>
                      <div className="m2m-reports-handoff__details">
                        <div className="m2m-reports-handoff__title-row">
                          <strong>{hasReport ? 'Deep Dive report' : 'Deep Dive order'}</strong>
                          <Tag color={statusColor(status)}>{statusLabel}</Tag>
                        </div>
                        <Text type="secondary">{targetLabel} · {dateLabel}</Text>
                      </div>
                    </div>
                    <div className="m2m-reports-handoff__actions">
                      {hasReport ? (
                        <Button
                          type="primary"
                          icon={<FileTextOutlined />}
                          aria-label={storedReport ? 'Open report' : 'Open report at Novel Publicity'}
                          onClick={() => storedReport ? setReportId(handoff.handoffId) : window.open(externalReport!, '_blank', 'noopener,noreferrer')}
                        >
                          {storedReport ? 'Open report' : 'Open report at Novel Publicity'}
                        </Button>
                      ) : null}
                      {canContinueCheckout ? (
                        <Button
                          icon={<ShoppingCartOutlined />}
                          onClick={() => window.open(String(checkoutUrl), '_blank', 'noopener,noreferrer')}
                        >
                          Continue checkout
                        </Button>
                      ) : null}
                      {isRetryable ? (
                        <Button icon={<RedoOutlined />} onClick={() => void retryHandoff(handoff.handoffId)}>
                          Retry
                        </Button>
                      ) : null}
                    </div>
                    {handoff.lastError ? (
                      <Alert
                        className="m2m-reports-handoff__error"
                        type="error"
                        showIcon
                        message="This report could not be completed. Retry it, or contact support if the problem continues."
                      />
                    ) : null}
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </section>
      ) : null}
      {reportId && <M2MReportViewer key={reportId} apiBase={apiBase} bookId={bookId} userId={userId} handoffId={reportId} onClose={() => setReportId(undefined)} />}
    </div>
  );
};

const M2MReportsModule: React.FC<M2MReportsModuleProps> = props => {
  const authorized = useM2MIdentity(props.userId);
  return authorized
    ? <M2MReportsWorkspace key={`${props.userId}:${props.bookId}:${props.apiBase}`} {...props} />
    : <Alert type="info" message="Sign in to access your Deep Dive reports." />;
};

export default M2MReportsModule;
